import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import axios from "../utils/axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Demo Requirement #5: Calendly integration for appointments
const AppointmentsPage = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm();

  const appointmentType = watch("appointmentType");

  // Fetch Calendly event types
  const { data: eventTypesData, isLoading: eventTypesLoading } = useQuery(
    ["event-types"],
    async () => {
      const response = await axios.get("/api/appointments/event-types");
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const eventTypes = eventTypesData?.eventTypes || [];

  // Fetch available time slots for selected date
  const { data: availableSlotsData, isLoading: slotsLoading } = useQuery(
    [
      "available-slots",
      selectedDate?.toISOString().split("T")[0],
      appointmentType,
    ],
    async () => {
      if (!selectedDate) return { availableSlots: [] };

      const dateStr = selectedDate.toISOString().split("T")[0];
      const typeParam = appointmentType || "consultation";
      const response = await axios.get(
        `/api/appointments/available-slots?date=${dateStr}&appointmentType=${encodeURIComponent(
          typeParam
        )}`
      );
      return response.data;
    },
    {
      enabled: !!selectedDate && !!appointmentType,
      refetchOnWindowFocus: false,
    }
  );

  const availableSlots = availableSlotsData?.availableSlots || [];

  const onSubmit = async (data) => {
    if (!selectedTime) {
      toast.error("Please select an appointment time");
      return;
    }

    setIsSubmitting(true);

    try {
      const appointmentDateTime = `${
        selectedDate.toISOString().split("T")[0]
      }T${selectedTime}:00`;

      const response = await axios.post("/api/appointments/book", {
        ...data,
        selectedTime: appointmentDateTime,
        appointmentType: data.appointmentType || "consultation",
      });

      // Check if this requires Calendly completion
      const appointment = response.data.appointment;
      const requiresCalendlyCompletion =
        appointment.calendly_link ||
        appointment.meetingLink?.includes("calendly.com");

      if (requiresCalendlyCompletion) {
        toast.success(
          "Thank you! Please check your email for the Calendly link to complete your booking.",
          { duration: 6000 }
        );
      } else {
        toast.success(
          "Appointment booked successfully! Check your email and WhatsApp for confirmation."
        );
      }

      reset();
      setSelectedTime("");

      // Redirect to thank you page
      navigate("/thank-you", {
        state: {
          message: requiresCalendlyCompletion
            ? "Thank you for your interest! Please check your email for the Calendly link to complete your booking."
            : "Appointment booked successfully!",
          appointmentId: appointment.id,
          calendlyLink: appointment.calendly_link || appointment.meetingLink,
          requiresCompletion: requiresCalendlyCompletion,
        },
      });
    } catch (error) {
      console.error("Booking error:", error);
      const errorMessage =
        error.response?.data?.message || "Booking failed. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter dates - only allow future dates and exclude weekends
  const filterDate = (date) => {
    const day = date.getDay();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Allow weekdays only and future dates
    return day !== 0 && day !== 6 && date >= today;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-2xl text-gray-900 mb-4 uppercase">

            Book Your Consultation
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Schedule a personalized consultation with our experts to discuss
            your business needs and discover how our solutions can help you
            achieve your goals.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-12">
          {/* Booking Form */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl text-gray-900 mb-6">
              Schedule Your Appointment
            </h2>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Appointment Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Appointment Type *
                </label>
                {eventTypesLoading ? (
                  <div className="text-center py-4">
                    <div className="loading-dots">
                      <div></div>
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      Loading appointment types...
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3">
                    {eventTypes.length > 0 ? (
                      eventTypes.map((eventType, index) => (
                        <label
                          key={eventType.id}
                          className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                        >
                          <input
                            type="radio"
                            value={eventType.id}
                            defaultChecked={index === 0}
                            {...register("appointmentType", {
                              required: "Please select appointment type",
                            })}
                            className="mr-3 text-primary-600"
                          />
                          <div>
                            <div className="font-medium text-gray-900">
                              {eventType.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {eventType.duration} minutes -{" "}
                              {eventType.description}
                            </div>
                          </div>
                        </label>
                      ))
                    ) : (
                      // Fallback to default options if no event types loaded
                      <>
                        <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                          <input
                            type="radio"
                            value="consultation"
                            defaultChecked={true}
                            {...register("appointmentType", {
                              required: "Please select appointment type",
                            })}
                            className="mr-3 text-primary-600"
                          />
                          <div>
                            <div className="font-medium text-gray-900">
                              Business Consultation
                            </div>
                            <div className="text-sm text-gray-500">
                              30 minutes - Discuss your business needs and our
                              solutions
                            </div>
                          </div>
                        </label>

                        <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                          <input
                            type="radio"
                            value="demo"
                            {...register("appointmentType")}
                            className="mr-3 text-primary-600"
                          />
                          <div>
                            <div className="font-medium text-gray-900">
                              Product Demo
                            </div>
                            <div className="text-sm text-gray-500">
                              15 minutes - See our products and services in
                              action
                            </div>
                          </div>
                        </label>
                      </>
                    )}
                  </div>
                )}
                {errors.appointmentType && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.appointmentType.message}
                  </p>
                )}
              </div>

              {/* Personal Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    {...register("name", {
                      required: "Name is required",
                      minLength: {
                        value: 2,
                        message: "Name must be at least 2 characters",
                      },
                    })}
                    className="input"
                    placeholder="Enter your full name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address",
                      },
                    })}
                    className="input"
                    placeholder="Enter your email"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.email.message}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Phone Number (Optional)
                </label>
                <input
                  type="tel"
                  id="phone"
                  {...register("phone")}
                  className="input"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              {/* Date Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Date *
                </label>
                <DatePicker
                  selected={selectedDate}
                  onChange={setSelectedDate}
                  filterDate={filterDate}
                  minDate={new Date()}
                  inline
                  className="w-full"
                />
              </div>

              {/* Time Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Time *
                </label>
                {!appointmentType ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <svg
                      className="w-12 h-12 mx-auto text-gray-400 mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="text-sm text-gray-600 font-medium">
                      Please select an appointment type first
                    </p>
                  </div>
                ) : slotsLoading ? (
                  <div className="text-center py-4">
                    <div className="loading-dots">
                      <div></div>
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      Loading available times...
                    </p>
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div className="text-center py-8 bg-yellow-50 rounded-lg border border-yellow-200">
                    <svg
                      className="w-12 h-12 mx-auto text-yellow-500 mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="text-sm text-gray-700 font-medium mb-1">
                      No available slots for this date
                    </p>
                    <p className="text-xs text-gray-600">
                      Try selecting a different date or use the Calendly widget
                      below
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {availableSlots.map((slot) => (
                      <button
                        key={slot.time}
                        type="button"
                        onClick={() => setSelectedTime(slot.time)}
                        className={`p-2 text-sm rounded-md border transition-all ${
                          selectedTime === slot.time
                            ? "bg-primary-600 text-white border-primary-600 shadow-md"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-primary-400"
                        }`}
                      >
                        {slot.time}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || !selectedTime}
                className="w-full btn-primary btn-lg"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="loading-dots mr-2">
                      <div></div>
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                    Booking...
                  </div>
                ) : (
                  "Book Appointment"
                )}
              </button>
            </form>
          </div>

          {/* Information Panel */}
          <div className="space-y-8">
            {/* What to Expect */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                What to Expect
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <svg
                    className="w-5 h-5 text-primary-600 mr-3 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <div className="font-medium text-gray-900">
                      Personalized Discussion
                    </div>
                    <div className="text-sm text-gray-600">
                      We'll discuss your specific business needs and challenges
                    </div>
                  </div>
                </li>

                <li className="flex items-start">
                  <svg
                    className="w-5 h-5 text-primary-600 mr-3 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <div className="font-medium text-gray-900">
                      Solution Recommendations
                    </div>
                    <div className="text-sm text-gray-600">
                      Get tailored recommendations for your business
                    </div>
                  </div>
                </li>

                <li className="flex items-start">
                  <svg
                    className="w-5 h-5 text-primary-600 mr-3 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <div className="font-medium text-gray-900">
                      Next Steps Planning
                    </div>
                    <div className="text-sm text-gray-600">
                      Create a clear action plan for moving forward
                    </div>
                  </div>
                </li>

                <li className="flex items-start">
                  <svg
                    className="w-5 h-5 text-primary-600 mr-3 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <div className="font-medium text-gray-900">No Pressure</div>
                    <div className="text-sm text-gray-600">
                      Educational conversation with no sales pressure
                    </div>
                  </div>
                </li>
              </ul>
            </div>

            {/* Contact Information */}
            <div className="bg-primary-50 rounded-xl p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Need Help?
              </h3>
              <p className="text-gray-600 mb-4">
                If you have any questions or need to reschedule, don't hesitate
                to contact us.
              </p>
              <div className="space-y-2">
                <div className="flex items-center text-gray-700">
                  <svg
                    className="w-5 h-5 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  support@tamilsandhai.com
                </div>
                <div className="flex items-center text-gray-700">
                  <svg
                    className="w-5 h-5 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  +1 (555) 123-4567
                </div>
              </div>
            </div>

            {/* Alternative Option */}
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Prefer a Quick Call?
              </h3>
              <p className="text-gray-600 mb-6">
                Join our 3-10 minute video sales call to get a quick overview of
                our solutions.
              </p>
              <button
                onClick={() => navigate("/register")}
                className="btn-primary btn-lg w-full"
              >
                Register for Quick Sales Call
              </button>
            </div>
          </div>
        </div>

        {/* Calendly Integration Section */}
        <div className="mt-16 bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-semibold text-gray-900 mb-4">
              📅 Or Book Directly with Calendly
            </h2>
            <p className="text-gray-600">
              Prefer to use Calendly? Book your appointment directly through our
              Calendly scheduling system below.
            </p>
          </div>

          {/* Calendly Inline Widget */}
          <div
            className="calendly-inline-widget"
            style={{ minWidth: "320px", height: "700px" }}
          >
            <iframe
              src={`${process.env.REACT_APP_CALENDLY_URL}?hide_event_type_details=1&hide_gdpr_banner=1`}
              width="100%"
              height="700"
              frameBorder="0"
              scrolling="no"
              title="Schedule Appointment"
              className="rounded-lg"
            ></iframe>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Can't see the calendar?{" "}
              <a
                href={process.env.REACT_APP_CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:underline"
              >
                Open in new window
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentsPage;
